/*
 * sr-hover-zoom.js
 *
 * SR FEATURE (2026-08-22) - hover-to-magnify on product gallery images.
 *
 * The theme already ships zoom: <product-gallery allow-zoom="3"> opens a
 * full-screen, pannable view at 3x when the image is CLICKED. That is a good
 * zoom, but it is a deliberate act - the visitor has to decide to look closer
 * before they can see the weave or the zari.
 *
 * Prakrithi (the reference the merchant asked about) uses Drift.js to magnify
 * on HOVER instead, at ~2.94x. Same magnification, no click. This adds that
 * behaviour without the library and without touching the click zoom, which
 * still works exactly as before - hover to browse detail, click to go
 * full-screen, the way Prakrithi's own page behaves.
 *
 * Notes
 * -----
 * - Desktop only. `(pointer: fine)` is evaluated per event, not once at boot,
 *   so a tablet that gains a mouse works and a touch tap never triggers it.
 *   On touch the click zoom remains the only zoom, which is correct - there is
 *   no hover on a finger.
 * - The magnified image is a Shopify CDN resize of the SAME file already on the
 *   page, requested at the width the magnifier needs. Nothing extra downloads
 *   until a visitor actually hovers, and once fetched it is reused.
 * - The overlay is `pointer-events: none`, so clicks, drags and the theme's own
 *   custom cursor pass straight through untouched.
 */
(function () {
  'use strict';

  var ZOOM = 3;              // matches the theme's allow-zoom="3"
  var MAX_CDN_WIDTH = 3200;  // largest rendition Shopify emits in the srcset here
  var MEDIA_SELECTOR = 'product-gallery .product-gallery__media';

  function pointerIsFine() {
    if (!window.matchMedia) return false;
    return window.matchMedia('(pointer: fine)').matches;
  }

  // Rewrite the CDN width parameter rather than guessing a URL: every image in
  // this gallery is already served as .../file.jpg?v=...&width=NNN, so asking
  // for a different width is a supported transform, not a hack.
  function highResUrl(img, cssWidth) {
    var src = img.currentSrc || img.src;
    if (!src) return null;
    var want = Math.min(MAX_CDN_WIDTH, Math.round(cssWidth * ZOOM * (window.devicePixelRatio > 1 ? 1.5 : 1)));
    if (/[?&]width=\d+/.test(src)) return src.replace(/([?&]width=)\d+/, '$1' + want);
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + want;
  }

  function ensureStyles() {
    if (document.getElementById('sr-hover-zoom-styles')) return;
    var css = document.createElement('style');
    css.id = 'sr-hover-zoom-styles';
    css.textContent =
      MEDIA_SELECTOR + '{position:relative}' +
      // z-index 1, deliberately BELOW the theme's own <custom-cursor
      // class="product-gallery__cursor">, which sits at z-index 2 in the shared
      // stacking context of .product-gallery__media-list-wrapper. At 3 the pane
      // painted over it and the magnifier cursor vanished while zooming. The
      // pane still covers the <img> because it is positioned and the image is
      // not, so DOM order wins there.
      '.sr-zoom-pane{position:absolute;inset:0;pointer-events:none;z-index:1;' +
        'background-repeat:no-repeat;background-color:#fff;opacity:0;transition:opacity .16s ease}' +
      '.sr-zoom-pane.is-on{opacity:1}';
    document.head.appendChild(css);
  }

  var active = null; // { media, img, pane, width, height }

  function teardown() {
    if (!active) return;
    var pane = active.pane;
    pane.classList.remove('is-on');
    // Let the fade finish before removing, but never leave an orphan behind if
    // the visitor moves on before it ends.
    window.setTimeout(function () {
      if (pane.parentNode) pane.parentNode.removeChild(pane);
    }, 200);
    active = null;
  }

  function setup(media, event) {
    var img = media.querySelector('img');
    if (!img || !img.naturalWidth) return;

    var rect = media.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    var url = highResUrl(img, rect.width);
    if (!url) return;

    ensureStyles();

    var pane = document.createElement('div');
    pane.className = 'sr-zoom-pane';
    pane.setAttribute('aria-hidden', 'true');
    pane.style.backgroundImage = 'url("' + url + '")';
    pane.style.backgroundSize = Math.round(rect.width * ZOOM) + 'px auto';
    media.appendChild(pane);

    active = { media: media, pane: pane, width: rect.width, height: rect.height };
    move(event);

    // Only reveal once the magnified file has decoded, so the visitor never
    // sees a blank white panel over the product while it loads.
    var probe = new Image();
    probe.onload = function () {
      if (active && active.pane === pane) pane.classList.add('is-on');
    };
    probe.src = url;
  }

  var lastX = 0, lastY = 0;

  function move(event) {
    if (!active) return;
    if (event) { lastX = event.clientX; lastY = event.clientY; }
    var rect = active.media.getBoundingClientRect();
    var x = (lastX - rect.left) / rect.width;
    var y = (lastY - rect.top) / rect.height;
    x = x < 0 ? 0 : x > 1 ? 1 : x;
    y = y < 0 ? 0 : y > 1 ? 1 : y;
    active.pane.style.backgroundPosition = (x * 100).toFixed(2) + '% ' + (y * 100).toFixed(2) + '%';
  }

  document.addEventListener('pointerover', function (event) {
    if (event.pointerType && event.pointerType !== 'mouse') return;
    if (!pointerIsFine()) return;
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;
    var media = target.closest(MEDIA_SELECTOR);
    if (!media) { teardown(); return; }
    if (active && active.media === media) return;
    teardown();
    setup(media, event);
  }, { passive: true });

  document.addEventListener('pointermove', function (event) {
    if (!active) return;
    if (event.pointerType && event.pointerType !== 'mouse') return;
    move(event);
  }, { passive: true });

  document.addEventListener('pointerout', function (event) {
    if (!active) return;
    var to = event.relatedTarget;
    if (to && typeof to.closest === 'function' && to.closest(MEDIA_SELECTOR) === active.media) return;
    teardown();
  }, { passive: true });

  // Scrolling with the cursor parked over the saree is the interaction the
  // merchant actually pointed at: the page moves, the pointer does not, and the
  // magnified region travels down the weave. So scroll must RECOMPUTE the pane,
  // never close it. The pane is absolutely positioned inside the media element,
  // so it rides along on its own - only the background offset needs redoing,
  // from the last known pointer position.
  document.addEventListener('scroll', function () {
    if (!active) return;
    var rect = active.media.getBoundingClientRect();
    // Once the image has left the viewport there is nothing to magnify.
    if (rect.bottom < 0 || rect.top > window.innerHeight) { teardown(); return; }
    move(null);
  }, { passive: true, capture: true });

  // A resize changes the pane's geometry and its correct background-size;
  // rebuilding on the next hover is simpler and cheaper than recalculating.
  window.addEventListener('resize', teardown, { passive: true });
})();
